import type { BusinessKind } from '../business-kind/business-kind-detection';
import { GBP_MEDIA_CATEGORIES, type GbpMediaCategory, type GbpMediaStatus } from './google-ranking-enums';

// Google photo library: photos enter from Instagram or a bulk upload, an AI check keeps the good ones and picks
// the Google category, and the photo autopilot sends a couple a week to the Google profile — no approval.

/** Statuses of a photo that is still in Hayven's library (not yet on its way to Google). */
export const GBP_LIBRARY_STATUSES = ['CHECKING', 'READY', 'SKIPPED', 'REMOVED'] as const satisfies readonly GbpMediaStatus[];
export type GbpLibraryStatus = (typeof GBP_LIBRARY_STATUSES)[number];

/**
 * What the AI check saw in a photo it kept: the stored superset of every kind. Each kind's check offers only
 * its own subjects (`gbpLibrarySubjectsFor`), so a kind's subject variety stays small and meaningful.
 */
export const GBP_LIBRARY_SUBJECTS = ['NAILS', 'HAIR', 'FOOD', 'WORK', 'INTERIOR', 'EXTERIOR', 'AT_WORK', 'TEAM'] as const;
export type GbpLibrarySubject = (typeof GBP_LIBRARY_SUBJECTS)[number];

const SUBJECTS_BY_KIND: Record<BusinessKind, readonly GbpLibrarySubject[]> = {
  NAIL: ['NAILS', 'INTERIOR', 'EXTERIOR', 'AT_WORK', 'TEAM'],
  HAIR: ['HAIR', 'INTERIOR', 'EXTERIOR', 'AT_WORK', 'TEAM'],
  RESTAURANT: ['FOOD', 'INTERIOR', 'EXTERIOR', 'TEAM'],
  OTHER: ['WORK', 'INTERIOR', 'EXTERIOR', 'AT_WORK', 'TEAM'],
};

/** The subjects the AI check may pick for a place of `kind` (nail: the original five). */
export function gbpLibrarySubjectsFor(kind: BusinessKind): readonly GbpLibrarySubject[] {
  return SUBJECTS_BY_KIND[kind];
}

/**
 * Google category a kept photo is filed under. Close-ups of the work (nails, hair, a business's results) count
 * as "at work"; a restaurant's dishes go under Google's own food & drink category.
 */
export const GBP_SUBJECT_TO_MEDIA_CATEGORY: Record<GbpLibrarySubject, GbpMediaCategory> = {
  NAILS: 'AT_WORK',
  HAIR: 'AT_WORK',
  FOOD: 'FOOD_AND_DRINK',
  WORK: 'AT_WORK',
  INTERIOR: 'INTERIOR',
  EXTERIOR: 'EXTERIOR',
  AT_WORK: 'AT_WORK',
  TEAM: 'TEAMS',
};

/** Owner label of each subject, worded for a nail salon; `gbpLibrarySubjectLabel` words it for any kind. */
export const GBP_LIBRARY_SUBJECT_LABELS: Record<GbpLibrarySubject, string> = {
  NAILS: 'Nails · close-up',
  HAIR: 'Hair · cuts & color',
  FOOD: 'Food & drinks',
  WORK: 'Our work',
  INTERIOR: 'Interior',
  EXTERIOR: 'Outside the salon',
  AT_WORK: 'Tech at work',
  TEAM: 'Team',
};

/** Where a kind words a shared subject differently from a nail salon. */
const KIND_SUBJECT_LABELS: Record<BusinessKind, Partial<Record<GbpLibrarySubject, string>>> = {
  NAIL: {},
  HAIR: { AT_WORK: 'Stylist at work' },
  RESTAURANT: { EXTERIOR: 'Outside the restaurant' },
  OTHER: { EXTERIOR: 'Outside', AT_WORK: 'Staff at work' },
};

/** "Tech at work" for a nail salon, "Stylist at work" for a hair salon, "Outside the restaurant"… */
export function gbpLibrarySubjectLabel(subject: GbpLibrarySubject, kind: BusinessKind): string {
  return KIND_SUBJECT_LABELS[kind][subject] ?? GBP_LIBRARY_SUBJECT_LABELS[subject];
}

export function isGbpLibrarySubject(value: string | null | undefined): value is GbpLibrarySubject {
  return (GBP_LIBRARY_SUBJECTS as readonly string[]).includes(value ?? '');
}

/** Google category for an AI subject; Google's catch-all when the subject is missing or unknown. */
export function gbpLibraryMediaCategory(subject: string | null | undefined): GbpMediaCategory {
  return isGbpLibrarySubject(subject) ? GBP_SUBJECT_TO_MEDIA_CATEGORY[subject] : 'ADDITIONAL';
}

/**
 * The Google category a photo goes (or went) under: the stored one (owner's pick on upload, or what was sent),
 * else its AI subject's. Sender and photo list share this one rule so a tile never shows another category.
 */
export function resolveGbpMediaCategory(photo: { category: string | null; subject: string | null }): GbpMediaCategory {
  const stored = photo.category ?? '';
  return (GBP_MEDIA_CATEGORIES as readonly string[]).includes(stored)
    ? (stored as GbpMediaCategory)
    : gbpLibraryMediaCategory(photo.subject);
}

/** Why the AI check left a photo out of the library. */
export const GBP_LIBRARY_SKIP_REASONS = ['TOO_MUCH_TEXT', 'BLURRY', 'NOT_SALON', 'DUPLICATE', 'TOO_SMALL', 'UNREADABLE'] as const;
export type GbpLibrarySkipReason = (typeof GBP_LIBRARY_SKIP_REASONS)[number];

/** A stored skip reason, or null when the column holds none (or an unknown one). */
export function toGbpLibrarySkipReason(value: string | null | undefined): GbpLibrarySkipReason | null {
  return (GBP_LIBRARY_SKIP_REASONS as readonly string[]).includes(value ?? '') ? (value as GbpLibrarySkipReason) : null;
}

/** Skip reasons the owner can overrule ("Use anyway"): Google would refuse the others outright. */
export const GBP_LIBRARY_OVERRULABLE_SKIP_REASONS = ['TOO_MUCH_TEXT', 'BLURRY', 'NOT_SALON', 'DUPLICATE'] as const satisfies readonly GbpLibrarySkipReason[];

export function canUseSkippedGbpPhotoAnyway(skipReason: string | null | undefined): boolean {
  return (GBP_LIBRARY_OVERRULABLE_SKIP_REASONS as readonly string[]).includes(skipReason ?? '');
}

export const GBP_LIBRARY_SKIP_REASON_LABELS: Record<GbpLibrarySkipReason, string> = {
  TOO_MUCH_TEXT: 'Too much text',
  BLURRY: 'Blurry',
  NOT_SALON: 'Not a salon photo',
  DUPLICATE: 'Same as another photo',
  TOO_SMALL: 'Too small for Google',
  UNREADABLE: "Couldn't read this photo",
};

/**
 * Photo autopilot: one library photo goes to Google on each of these salon-local weekdays (0 = Sunday) at
 * `hourLocal`; Up next shows the slots of the next `horizonDays`.
 */
export const GBP_PHOTO_AUTOPILOT = { weekdays: [1, 4], hourLocal: 11, horizonDays: 14 } as const;

export const GBP_LIBRARY_LIMITS = {
  /** Photos in one bulk upload. */
  bulkUploadMax: 100,
  /** The first Instagram import reaches back this many days, and at most this many posts. */
  instagramFirstImportDays: 60,
  instagramFirstImportMaxMedia: 50,
  /** Two photos whose 64-bit dHash differ in at most this many bits are the same photo. */
  dHashMaxDistance: 6,
  /** Photos one salon can upload to its library per (salon-local) day — storage + AI-check cost guard. */
  uploadsPerDay: 300,
  /** Photos per library page. */
  listPageSize: 60,
  /** Photo ids one bulk-upload status lookup may ask about. */
  statusLookupMax: 100,
  /** Ready library photos the owner may send to Google at once ("Send N photos now"). */
  sendNowMax: 5,
  /**
   * Photos "Send now" may send per (salon-local) day, so repeated taps cannot empty the library onto the
   * listing at once (the autopilot spaces photos out on purpose).
   */
  sendNowPerDay: 10,
} as const;

/**
 * Library page tabs: AI-approved photos waiting for their turn, photos on (or on their way to) Google incl.
 * those shown in a live post, photos the check left out, and photos the owner removed.
 */
export const GBP_LIBRARY_TABS = ['ready', 'google', 'skipped', 'removed'] as const;
export type GbpLibraryTab = (typeof GBP_LIBRARY_TABS)[number];
